import { createContext, ReactNode } from 'react';

import { DEFAULT_REPORT_CONFIG, ReportConfig } from '@app-velocity/common';
import { TAiSimulationPartialDto } from '@app-velocity/av-api-v2';

import {
  PathPreviewRenderProps,
  PrototypeConfusions,
  ReportMainData,
  ReportStatistics,
  RunStepExplanationProps,
  SinglePath,
  SinglePathView,
} from '../types';

export type ReportContextType = {
  data: ReportMainData | null;
  baseURL: string;
  type: 'figma' | 'vibe';
  ownerId: string;
  reportURL: string;
  simulationURL?: string;
  isLoading: boolean;
  config: {
    humanParticipantsAllowed: boolean;
    hasAccessToSims: boolean;
    canRunSimulations?: (amount: number) => void;
  };
  reportConfig: {
    config: ReportConfig;
  };
  statistics: ReportStatistics;
  paths: SinglePath[];
  confusions?: PrototypeConfusions | null;
  pendingSimulations?: (
    | 'EXTENDED'
    | 'EXTENDED_3'
    | 'EXTENDED_50'
    | 'EXTENDED_100'
    | 'SINGLE'
  )[];
  renderStepView: (
    pageId: string,
    partials: TAiSimulationPartialDto[],
    clicks: { x: number; y: number }[],
    size: 'full' | 'large' | 'normal',
    targetHeight: number,
    persona: 'accessible' | 'default' | string,
    image?: string,
    width?: number,
    height?: number,
    highlight?: boolean
  ) => ReactNode;
  renderRunStepExplanation?: (props: RunStepExplanationProps) => ReactNode;
  renderPathPreview: (props: PathPreviewRenderProps) => ReactNode;
  renderRequirements: (() => ReactNode) | null;
  renderPathDebug?: (
    view: SinglePathView,
    viewIndex: number,
    path: SinglePath,
    pathNumber: number
  ) => ReactNode;
  shareReportButton?: ReactNode;
};

export const ReportContext = createContext<ReportContextType>({
  data: null,
  type: 'figma',
  ownerId: '',
  baseURL: '',
  reportURL: '',
  isLoading: false,
  config: {
    humanParticipantsAllowed: false,
    hasAccessToSims: false,
  },
  reportConfig: {
    config: DEFAULT_REPORT_CONFIG,
  },
  statistics: {} as ReportStatistics,
  paths: [],
  renderStepView: (
    pageId: string,
    partials: TAiSimulationPartialDto[],
    clicks: { x: number; y: number }[],
    size: 'full' | 'large' | 'normal',
    targetHeight: number,
    persona: 'accessible' | 'default' | string,
    image?: string,
    width?: number,
    height?: number,
    highlight?: boolean
  ) => null,
  renderPathPreview: () => null,
  renderRequirements: null,
});
